import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  createForwardingAddress,
} from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { ForwardingAddressModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { emailRegex } from '../../../../../utils/regular-expressions';
import { Messages } from '../../../../../utils/message-constants';

import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grow from '@mui/material/Grow';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface AddForwardingAddressProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  forwardingEmails: ForwardingAddressModel[];
  primaryDomain: string;
  changeLogRoles: string[];
}

export default function AddForwardingAddressDialog({
  open,
  handleClose,
  userEmail,
  forwardingEmails,
  primaryDomain,
  changeLogRoles,
}: AddForwardingAddressProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [forwardingAddressEmail, setForwardingAddressEmail] = useState('');
  const [externalEmailCheckBox, setExternalEmailCheckBox] = useState(false);
  const [externalEmailError, setExternalEmailError] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  useEffect(() => {
    if (!open) {
      setForwardingAddressEmail('');
      setExternalEmailError(false);
      setExternalEmailCheckBox(false);
      setHasError(false);
    }
  }, [open]);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      createForwardingAddress(
        newForwardingAddress,
        userEmail,
        changeLogMessage
      ),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Forwarding address'),
      });

      const newList = [...forwardingEmails, data];

      queryClient.setQueryData(
        [UserKeys.ForwardingAddresses, userEmail],
        newList
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating(
          'the forwarding address'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .create_forwarding_address,
        {
          module: ModuleList.modules.userManagement.name,
          section:
            ModuleList.modules.userManagement.sections.forwardingAddresses,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const newForwardingAddress = {
    forwardingEmail: forwardingAddressEmail,
  } as ForwardingAddressModel;

  function handleSubmit() {
    if (!emailRegex.test(forwardingAddressEmail)) {
      setHasError(true);
    } else if (
      forwardingAddressEmail.split('@')[1] !== primaryDomain &&
      externalEmailCheckBox === false
    ) {
      setHasError(false);
      setExternalEmailError(true);
    } else {
      setHasError(false);
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.AddGmailForwardAddress.value
        )
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Add Forwarding Address"
      dialogContent={
        <Stack gap={2}>
          <TextField
            autoFocus
            id="forwardingAddressEmail"
            label="Forwarding Address Email"
            placeholder="Enter forwarding address"
            fullWidth
            error={hasError}
            helperText={hasError ? 'Must be a valid email address.' : ''}
            value={forwardingAddressEmail}
            onChange={(event) => setForwardingAddressEmail(event.target.value)}
          />
          <FormControlLabel
            sx={{ width: 'fit-content' }}
            control={
              <Checkbox
                checked={externalEmailCheckBox}
                onChange={(e) => {
                  if (e.target.checked) {
                    setExternalEmailCheckBox(true);
                  } else {
                    setExternalEmailCheckBox(false);
                  }
                }}
              />
            }
            label={<Typography>Confirm adding external address</Typography>}
          />
          <Grow in={externalEmailError}>
            <Alert severity="info">
              If this is an external address, please confirm you wish to add
              them as a forwarding address. They will receive a confirmation
              email.
            </Alert>
          </Grow>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Add Address',
        action: handleSubmit,
        loading: isPending,
        disabled: forwardingAddressEmail.length === 0,
      }}
    />
  );
}
